/**
 * Authentication Context
 * Manages authentication state and Keycloak integration
 * Matches portal project's auth mechanism:
 * - Email/password login via Keycloak direct grant
 * - Signup via backend (creates user in Keycloak)
 * - Google OAuth via Keycloak IdP brokering (PKCE)
 * - Token refresh with automatic rotation
 * - Role-based access control
 * - Session expiry tracking for UI timer
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { DecodedToken, AuthContextType } from '@/types/auth';
import { authAPI } from '@/lib/api/auth';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const processToken = (decoded: DecodedToken): DecodedToken => {
  if (!decoded.realm_access) {
    decoded.realm_access = { roles: [] };
  }
  const roles = decoded.realm_access.roles || [];
  const hasDefinedRole = roles.some((r) =>
    ['CUSTOMER', 'SELLER', 'ADMIN'].includes(r)
  );
  if (!hasDefinedRole) {
    decoded.realm_access.roles.push('CUSTOMER');
  }
  // Pick highest-priority role (ADMIN > SELLER > CUSTOMER)
  decoded.role = ['ADMIN', 'SELLER', 'CUSTOMER'].find((r) =>
    roles.includes(r)
  );
  return decoded;
};

/** Extract expiry timestamp (ms) from a stored access token */
const getTokenExpiry = (token: string | null): number | null => {
  if (!token) return null;
  try {
    const decoded = jwtDecode<{ exp?: number }>(token);
    return decoded.exp ? decoded.exp * 1000 : null;
  } catch {
    return null;
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<DecodedToken | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionExpiresAt, setSessionExpiresAt] = useState<number | null>(null);

  // Shared promise to prevent concurrent refresh calls (Keycloak rotates refresh tokens)
  const refreshPromiseRef = React.useRef<Promise<boolean> | null>(null);

  // --- Helper: update auth state from a new access token string ---
  const applyToken = React.useCallback((token: string, newRefreshToken?: string) => {
    const decoded = processToken(jwtDecode<DecodedToken>(token));
    localStorage.setItem('accessToken', token);
    if (newRefreshToken) {
      localStorage.setItem('refreshToken', newRefreshToken);
    }
    setAccessToken(token);
    setUser(decoded);
    setIsAuthenticated(true);
    setSessionExpiresAt(decoded.exp ? decoded.exp * 1000 : null);
  }, []);

  // --- Token refresh (deduplicated — only one in-flight request at a time) ---
  const refreshAccessToken = React.useCallback(async (): Promise<boolean> => {
    // If a refresh is already in progress, reuse that promise
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return false;

    const doRefresh = async (): Promise<boolean> => {
      try {
        const data = await authAPI.refreshToken(refreshToken);
        if (!data || !data.access_token) {
          // Refresh failed, clear everything
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          setAccessToken(null);
          setUser(null);
          setIsAuthenticated(false);
          setSessionExpiresAt(null);
          return false;
        }

        applyToken(data.access_token, data.refresh_token);
        return true;
      } catch {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        setAccessToken(null);
        setUser(null);
        setIsAuthenticated(false);
        setSessionExpiresAt(null);
        return false;
      } finally {
        refreshPromiseRef.current = null;
      }
    };

    refreshPromiseRef.current = doRefresh();
    return refreshPromiseRef.current;
  }, [applyToken]);

  // --- Check and refresh token if expiring soon ---
  const checkAndRefreshToken = React.useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    try {
      const expiry = getTokenExpiry(token);
      if (!expiry) return;

      const timeUntilExpiry = expiry - Date.now();

      // Update sessionExpiresAt so the timer stays in sync
      setSessionExpiresAt(expiry);

      if (timeUntilExpiry < 120000 && timeUntilExpiry > 0) {
        // Expiring in less than 2 minutes — proactively refresh
        await refreshAccessToken();
      } else if (timeUntilExpiry <= 0) {
        // Already expired — try refresh
        await refreshAccessToken();
      }
    } catch {
      // Ignore errors
    }
  }, [refreshAccessToken]);

  // --- Initialize auth from stored token ---
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('accessToken');
      if (token) {
        try {
          const decoded = processToken(jwtDecode<DecodedToken>(token));

          if (decoded.exp && decoded.exp * 1000 > Date.now()) {
            setAccessToken(token);
            setUser(decoded);
            setIsAuthenticated(true);
            setSessionExpiresAt(decoded.exp * 1000);
          } else {
            // Token expired, try refresh
            await refreshAccessToken();
          }
        } catch {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
        }
      }
      setLoading(false);
    };

    initAuth();

    // Check and refresh token every 30 seconds (more aggressive than before)
    const tokenCheckInterval = setInterval(() => {
      const token = localStorage.getItem('accessToken');
      if (token) {
        checkAndRefreshToken();
      }
    }, 30 * 1000);

    return () => clearInterval(tokenCheckInterval);
  }, [checkAndRefreshToken, refreshAccessToken]);

  // --- Listen for API client events (token-refreshed / auth-session-expired) ---
  useEffect(() => {
    const handleTokenRefreshed = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.access_token) {
        try {
          const decoded = processToken(jwtDecode<DecodedToken>(detail.access_token));
          setAccessToken(detail.access_token);
          setUser(decoded);
          setIsAuthenticated(true);
          setSessionExpiresAt(decoded.exp ? decoded.exp * 1000 : null);
        } catch {
          // ignore decode errors
        }
      }
    };

    const handleSessionExpired = () => {
      setAccessToken(null);
      setUser(null);
      setIsAuthenticated(false);
      setSessionExpiresAt(null);
    };

    window.addEventListener('token-refreshed', handleTokenRefreshed);
    window.addEventListener('auth-session-expired', handleSessionExpired);

    return () => {
      window.removeEventListener('token-refreshed', handleTokenRefreshed);
      window.removeEventListener('auth-session-expired', handleSessionExpired);
    };
  }, []);

  // --- Login with email/password ---
  const login = async (email: string, password: string, _redirectTo?: string) => {
    const normalizedEmail = email.trim().toLowerCase();

    try {
      setError(null);
      const data = await authAPI.login(normalizedEmail, password);
      applyToken(data.access_token, data.refresh_token);
      // Note: Role-based redirect is handled by the Auth page component
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      setError(errorMessage);
      throw err;
    }
  };

  // --- Signup via backend ---
  const signup = async (
    email: string,
    password: string,
    role: string = 'CUSTOMER',
    _redirectTo?: string,
    firstName?: string,
    lastName?: string,
    phone?: string
  ) => {
    try {
      setError(null);
      await authAPI.signup(email, password, role, firstName, lastName, phone);
      // Don't auto-login — user must verify email first
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Signup failed';
      setError(errorMessage);
      throw err;
    }
  };

  // --- Google login via Keycloak IdP ---
  const googleLogin = async () => {
    try {
      setError(null);
      await authAPI.initiateGoogleLogin();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to initiate Google login';
      setError(errorMessage);
    }
  };

  // --- Handle OAuth callback (exchange code for tokens) ---
  const handleAuthCallback = async (code: string): Promise<boolean> => {
    try {
      setError(null);
      const data = await authAPI.exchangeCodeForToken(code);
      applyToken(data.access_token, data.refresh_token);
      return true;
    } catch (err) {
      console.error('Auth callback failed', err);
      setError(err instanceof Error ? err.message : 'Authentication failed');
      throw err;
    }
  };

  // --- Logout ---
  const logout = React.useCallback(async () => {
    try {
      await authAPI.logout();
    } catch {
      // Always clear local state even if Keycloak logout fails
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      setAccessToken(null);
      setUser(null);
      setIsAuthenticated(false);
      setSessionExpiresAt(null);
      setError(null);
    }
  }, []);

  // --- Role check ---
  const hasRole = (role: string) => {
    return user?.realm_access?.roles?.includes(role) ?? false;
  };

  // --- Manual refresh (for "Stay logged in" button) ---
  const manualRefresh = React.useCallback(async (): Promise<boolean> => {
    return refreshAccessToken();
  }, [refreshAccessToken]);

  const value: AuthContextType = {
    isAuthenticated,
    user,
    accessToken,
    login,
    signup,
    logout,
    hasRole,
    googleLogin,
    handleAuthCallback,
    loading,
    error,
    sessionExpiresAt,
    manualRefresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
